import { useState, useEffect } from 'react';
import axios from 'axios';
import { Slider } from './ui/slider';
import { Button } from './ui/button';
import { Plus, Minus, FloppyDisk, Check, Gift } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_HOURS = 50;          // upper bound of the slider
const STEP = 0.5;              // half-hour granularity

// Pretty-print hours: 1 → "1 uur", 1.5 → "1,5 uur", 0.5 → "½ uur"
function formatHours(n) {
    if (!n) return '0 uur';
    if (n === 0.5) return '½ uur';
    const isHalf = n % 1 !== 0;
    const str = isHalf ? n.toString().replace('.', ',') : String(n);
    return `${str} uur`;
}

// EUR formatter that drops trailing ",00"
function formatEuro(amount) {
    if (!amount && amount !== 0) return '';
    const fixed = amount.toFixed(2);
    const clean = fixed.endsWith('.00') ? fixed.slice(0, -3) : fixed.replace('.', ',');
    return `€${clean}`;
}

/**
 * Generic hours-based slider for both:
 *  - paid extra lessons  (field="extra_lessons", color=gold)
 *  - complimentary free lessons (field="free_lessons", color=emerald)
 *
 * 1 lesson = 1 hour. Step = 0.5 → half-hour lessons supported.
 */
export default function ExtraLessonsSlider({
    studentId,
    initialValue = 0,
    onSaved,
    field = 'extra_lessons',
    title,
    subtitle,
    color = 'gold',          // 'gold' | 'emerald'
    pricePerLesson = null,   // null hides the price column (e.g. for free lessons)
    icon = null,
}) {
    const [value, setValue] = useState(Number(initialValue) || 0);
    const [savedValue, setSavedValue] = useState(Number(initialValue) || 0);
    const [saving, setSaving] = useState(false);
    const [savedFlag, setSavedFlag] = useState(false);

    useEffect(() => {
        const v = Number(initialValue) || 0;
        setValue(v);
        setSavedValue(v);
    }, [initialValue]);

    const dirty = value !== savedValue;
    const tick = (delta) => Math.max(0, Math.min(MAX_HOURS, +(value + delta).toFixed(1)));

    const save = async () => {
        if (saving) return;
        setSaving(true);
        try {
            await axios.put(
                `${API}/students/${studentId}`,
                { [field]: value },
                { withCredentials: true }
            );
            setSavedValue(value);
            setSavedFlag(true);
            if (onSaved) onSaved(value);
            setTimeout(() => setSavedFlag(false), 2000);
        } catch (e) {
            console.error(`save ${field}`, e);
        } finally {
            setSaving(false);
        }
    };

    // Color palette (NOTE: Tailwind needs literal class names — no string interpolation)
    const isEmerald = color === 'emerald';
    const palette = isEmerald
        ? {
            counter: 'text-emerald-400',
            saveBtn: 'bg-emerald-500 hover:bg-emerald-400 text-[#1A202C]',
            chip: 'bg-emerald-500/15 border-emerald-400/40 text-emerald-300',
        }
        : {
            counter: 'text-[#D4A537]',
            saveBtn: 'bg-[#D4A537] hover:bg-[#E5B84A] text-[#1A202C]',
            chip: 'bg-[#D4A537]/15 border-[#D4A537]/40 text-[#D4A537]',
        };
    const sliderClass = isEmerald
        ? 'flex-1 [&>span:first-child]:h-2 [&>span:first-child]:bg-[#4A5568] [&_[role=slider]]:bg-emerald-400 [&_[role=slider]]:border-emerald-400 [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&>span:first-child_span]:bg-emerald-500'
        : 'flex-1 [&>span:first-child]:h-2 [&>span:first-child]:bg-[#4A5568] [&_[role=slider]]:bg-[#D4A537] [&_[role=slider]]:border-[#D4A537] [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&>span:first-child_span]:bg-[#D4A537]';

    const totalPrice = pricePerLesson != null ? value * pricePerLesson : null;
    const halfPrice = pricePerLesson != null ? pricePerLesson / 2 : null;
    const IconCmp = icon;

    return (
        <div
            className="mt-4 bg-[#1A202C] rounded-lg p-4 border border-[#4A5568]"
            data-testid={`${field === 'free_lessons' ? 'free' : 'extra'}-lessons-slider`}
        >
            <div className="flex items-center justify-between mb-3 gap-3">
                <div className="min-w-0">
                    <div className="text-xs text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                        {IconCmp && <IconCmp size={14} weight="fill" className={palette.counter} />}
                        {title || 'Extra lessen'}
                    </div>
                    <div className="text-sm text-gray-300 truncate">
                        {subtitle || 'Bijgekochte lessen bovenop het pakket'}
                    </div>
                </div>
                <div className="text-right flex-shrink-0">
                    <div className={`text-2xl sm:text-3xl font-bold tabular-nums ${value > 0 ? palette.counter : 'text-white'}`}>
                        +{formatHours(value)}
                    </div>
                    {pricePerLesson != null && value > 0 ? (
                        <div className={`mt-0.5 inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 border ${palette.chip}`} data-testid={`${field}-price`}>
                            {formatEuro(totalPrice)}
                        </div>
                    ) : (
                        <div className="text-[10px] text-gray-500 uppercase">half = 30 min</div>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-3">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setValue(tick(-STEP))}
                    disabled={value <= 0}
                    className="h-9 w-9 p-0 border-[#4A5568] bg-[#2D3748] text-gray-300 hover:bg-[#3D4A5C] hover:text-white disabled:opacity-30"
                    data-testid={`${field}-minus`}
                >
                    <Minus size={16} weight="bold" />
                </Button>
                <Slider
                    min={0}
                    max={MAX_HOURS}
                    step={STEP}
                    value={[value]}
                    onValueChange={(v) => setValue(v[0])}
                    className={sliderClass}
                    data-testid={`${field}-slider`}
                />
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setValue(tick(STEP))}
                    disabled={value >= MAX_HOURS}
                    className="h-9 w-9 p-0 border-[#4A5568] bg-[#2D3748] text-gray-300 hover:bg-[#3D4A5C] hover:text-white disabled:opacity-30"
                    data-testid={`${field}-plus`}
                >
                    <Plus size={16} weight="bold" />
                </Button>
            </div>

            <div className="flex items-center justify-between mt-2 text-[11px] text-gray-500">
                <span>0</span>
                {pricePerLesson != null && halfPrice != null && value > 0 ? (
                    <span className="text-gray-400" data-testid={`${field}-price-breakdown`}>
                        1 les = {formatEuro(pricePerLesson)} · ½ uur = {formatEuro(halfPrice)}
                    </span>
                ) : pricePerLesson != null && halfPrice != null ? (
                    <span className="text-gray-500">
                        1 les = {formatEuro(pricePerLesson)} · ½ uur = {formatEuro(halfPrice)}
                    </span>
                ) : null}
                <span>{MAX_HOURS}</span>
            </div>

            {(dirty || savedFlag) && (
                <div className="flex items-center justify-end gap-2 mt-2">
                    {savedFlag && !dirty ? (
                        <span className="text-xs text-emerald-400 flex items-center gap-1" data-testid={`${field}-saved`}>
                            <Check size={12} weight="bold" /> Opgeslagen
                        </span>
                    ) : (
                        <Button
                            size="sm"
                            onClick={save}
                            disabled={saving}
                            className={`h-7 px-3 ${palette.saveBtn} font-semibold`}
                            data-testid={`${field}-save`}
                        >
                            <FloppyDisk size={14} className="mr-1" />
                            {saving ? 'Opslaan...' : 'Opslaan'}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}

// Convenience wrapper for the free (complimentary) lessons slider
export function FreeLessonsSlider(props) {
    return (
        <ExtraLessonsSlider
            field="free_lessons"
            title="Gratis lessen"
            subtitle="Cadeau-lessen die je weggeeft (kosten niets)"
            color="emerald"
            pricePerLesson={null}
            icon={Gift}
            {...props}
        />
    );
}
